import { useState, useCallback } from 'react';
import { DocumentIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface DocumentUploadStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function DocumentUploadStep({ onNext, onBack }: DocumentUploadStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { requestId, idDocumentId, setIdDocumentId, setStep } = useVerificationStore();

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !requestId) return;

      // Validate file
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a JPEG, PNG, WebP, or PDF file');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setError(null);
      setIsUploading(true);
      setUploadedFile(file);

      try {
        const response = await verificationApi.uploadDocument(requestId, file, 'IdFront');
        setIdDocumentId(response.data.documentId);
      } catch (err: unknown) {
        const error = err as { response?: { data?: { error?: string } } };
        setError(error.response?.data?.error || 'Failed to upload document');
        setUploadedFile(null);
      } finally {
        setIsUploading(false);
      }
    },
    [requestId, setIdDocumentId]
  );

  const handleRemove = () => {
    setUploadedFile(null);
    setIdDocumentId('');
  };

  const handleContinue = () => {
    if (!idDocumentId) {
      setError('Please upload your ID document');
      return;
    }
    setStep('selfie');
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Upload ID Document</h2>
        <p className="text-sm text-gray-600 mt-1">
          Please upload a clear photo or scan of your South African ID document (Smart ID card or
          green ID book).
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="space-y-4">
        {!idDocumentId ? (
          <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {isUploading ? (
                <LoadingSpinner size="lg" />
              ) : (
                <>
                  <DocumentIcon className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Tap to upload</span>
                    <span className="hidden sm:inline"> or drag and drop</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    JPEG, PNG, WebP, or PDF (max 10MB)
                  </p>
                </>
              )}
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </label>
        ) : (
          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-medium text-gray-900">{uploadedFile?.name || 'ID Document'}</p>
                <p className="text-sm text-green-600">Uploaded successfully</p>
              </div>
            </div>
            <button
              onClick={handleRemove}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="Remove file"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Tips for a good photo:</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Ensure all four corners of the ID are visible</li>
            <li>Make sure the text is clear and readable</li>
            <li>Avoid glare or reflections on the document</li>
            <li>Use good lighting</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button type="button" onClick={onBack} className="btn-secondary">
          Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          className="btn-primary"
          disabled={!idDocumentId}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
